import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagsEditor } from "@/shared/components/TagsEditor";
import { type ActionRecord } from "@/shared/domain/actions";
import { useUpdateAction } from "@/shared/domain/actions/hooks";

interface UpdateActionTagsProps {
  /** The action whose tags are shown and edited. */
  action: ActionRecord;
  /** Org the action belongs to, which each save is made as. */
  resourceOwnerId: string;
  /**
   * Shows the tags with no way to change them, for a viewer who may not edit
   * the action.
   * @default false
   */
  readonly?: boolean;
}

/**
 * An action's tags in the shared {@link TagsEditor}, each change saved as a
 * metadata changeset. The saved record goes into the query cache, so the
 * action's page shows the tags the server kept.
 */
export function UpdateActionTags({
  action,
  resourceOwnerId,
  readonly = false,
}: UpdateActionTagsProps) {
  const { mutateAsync: updateAction } = useUpdateAction();

  const save = (metadata_changeset: {
    put_tags?: string[];
    remove_tags?: string[];
  }) =>
    updateAction({
      name: action.name,
      resourceOwnerId,
      updates: { metadata_changeset },
    });

  return (
    <TagsEditor
      tags={action.tags ?? []}
      recordKey={action.name}
      readonly={readonly}
      autocompleteType={AutocompleteType.ActionTags}
      onAdd={(tag) => save({ put_tags: [tag] })}
      onRemove={(tag) => save({ remove_tags: [tag] })}
      analytics={{
        add: "AddActionTagClicked",
        remove: "RemoveActionTagClicked",
        props: { name: action.name },
      }}
    />
  );
}
